import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import {
  Brain, ArrowLeft, Loader2, Lock, Sparkles, AlertTriangle, Heart,
  RefreshCw, ShieldQuestion, Crown, Share2, X, Copy, TrendingUp, Scale,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";
import { celebrate } from "@/lib/feedback";
import { APP_DOMAIN, APP_URL } from "@/config";
import SocialProof from "@/components/SocialProof";

const LEVEL = {
  high: { c: "#ef4444", bg: "bg-red-500", label: "Élevé" },
  medium: { c: "#f59e0b", bg: "bg-amber-400", label: "Moyen" },
  low: { c: "#52525b", bg: "bg-zinc-600", label: "Faible" },
};

// Per-archetype visual theme for the shareable story card (unique look per result).
const THEMES = {
  love_bombing:        { emoji: "💣", g: ["#2a0a1e", "#5b0f2e", "#7a1533"], glow: ["rgba(244,63,94,.45)", "rgba(236,72,153,.4)"], accent: "#fb7185", rs: "#fda4af", rf: "#f43f5e" },
  gaslighting:         { emoji: "🌀", g: ["#0c0a1f", "#241056", "#3b1a6b"], glow: ["rgba(139,92,246,.45)", "rgba(99,102,241,.4)"], accent: "#c4b5fd", rs: "#c4b5fd", rf: "#8b5cf6" },
  pervers_narcissique: { emoji: "🎭", g: ["#120306", "#3a0a12", "#5c0f14"], glow: ["rgba(239,68,68,.5)", "rgba(24,24,27,.6)"], accent: "#fca5a5", rs: "#fca5a5", rf: "#dc2626" },
  devalorisation:      { emoji: "🧊", g: ["#04121f", "#0b2a45", "#123b5e"], glow: ["rgba(56,189,248,.4)", "rgba(59,130,246,.4)"], accent: "#7dd3fc", rs: "#7dd3fc", rf: "#0ea5e9" },
  intermittence:       { emoji: "🎢", g: ["#241004", "#4a2205", "#6b3410"], glow: ["rgba(245,158,11,.45)", "rgba(249,115,22,.4)"], accent: "#fcd34d", rs: "#fcd34d", rf: "#f59e0b" },
  culpabilisation:     { emoji: "🥺", g: ["#041916", "#0b3b34", "#124b40"], glow: ["rgba(20,184,166,.4)", "rgba(16,185,129,.4)"], accent: "#5eead4", rs: "#5eead4", rf: "#14b8a6" },
  controle_possessif:  { emoji: "🔒", g: ["#1a0406", "#450a0f", "#6b0f14"], glow: ["rgba(220,38,38,.5)", "rgba(120,20,20,.5)"], accent: "#fca5a5", rs: "#fca5a5", rf: "#b91c1c" },
  evitement:           { emoji: "🌫️", g: ["#0c0e12", "#232a33", "#333d47"], glow: ["rgba(148,163,184,.4)", "rgba(100,116,139,.4)"], accent: "#cbd5e1", rs: "#cbd5e1", rf: "#64748b" },
  dependance:          { emoji: "🧲", g: ["#1e0620", "#4a0f45", "#6b1560"], glow: ["rgba(217,70,239,.45)", "rgba(168,85,247,.4)"], accent: "#f0abfc", rs: "#f0abfc", rf: "#d946ef" },
  healthy:             { emoji: "💚", g: ["#04160f", "#0b3a26", "#124b30"], glow: ["rgba(16,185,129,.45)", "rgba(52,211,153,.4)"], accent: "#6ee7b7", rs: "#6ee7b7", rf: "#10b981" },
};

const LABEL_TO_KEY = {
  "Love bombing": "love_bombing",
  "Gaslighting / manipulation": "gaslighting",
  "Tendances pervers narcissique": "pervers_narcissique",
  "Dévalorisation / mépris": "devalorisation",
  "Intermittence / breadcrumbing": "intermittence",
  "Culpabilisation / victimisation": "culpabilisation",
  "Possessivité / contrôle": "controle_possessif",
  "Évitement affectif": "evitement",
  "Dépendance affective": "dependance",
};

function archetypeTheme(p) {
  let key = "healthy";
  const arch = p.archetypes || [];
  if (arch.length) {
    const top = arch.slice().sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    if (top && (top.score || 0) >= 34) key = top.key;
  } else if (p.teaser_archetype && p.teaser_archetype.level !== "low") {
    key = LABEL_TO_KEY[p.teaser_archetype.label] || "healthy";
  }
  return THEMES[key] || THEMES.healthy;
}

// Style variants for the story card (tap "Changer le style" to cycle).
const VARIANTS = [
  { bg: (g) => `linear-gradient(160deg,${g[0]} 0%,${g[1]} 55%,${g[2]} 100%)`, a: { top: -64, right: -64 }, b: { bottom: -80, left: -64 } },
  { bg: (g) => `linear-gradient(35deg,${g[0]} 0%,${g[1]} 50%,${g[2]} 100%)`, a: { top: -64, left: -64 }, b: { bottom: -80, right: -64 } },
  { bg: (g) => `radial-gradient(130% 90% at 50% 0%,${g[2]} 0%,${g[1]} 45%,${g[0]} 100%)`, a: { top: -70, left: 120 }, b: { bottom: -90, right: 110 } },
];

export default function PsychProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qp = new URLSearchParams(location.search);
  const [target, setTarget] = useState(qp.get("target") === "me" ? "me" : "them");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refCode, setRefCode] = useState(null);
  const [compare, setCompare] = useState(qp.get("compare") === "1");

  useEffect(() => {
    api.get("/referral/me").then((r) => setRefCode(r.data?.code)).catch(() => {});
  }, []);

  const load = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/profile/${id}?target=${t}`);
      setData(data);
    } catch (e) {
      toast.error("Impossible de charger le profil");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(target); /* eslint-disable-next-line */ }, [id, target]);

  const generate = async () => {
    setGenerating(true); vibrate();
    try {
      const { data: res } = await api.post(`/profile/${id}`, { target });
      setData((d) => ({ ...(d || {}), profile: res.profile, generated_at: res.generated_at, message_count: res.message_count }));
      celebrate();
      toast.success("Profil généré 🧠");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "not_enough_messages") toast.error(det.message);
      else toast.error(det || "Analyse impossible");
    } finally { setGenerating(false); }
  };

  const p = data?.profile;
  const targetName = data?.contact_name || "l'autre";

  return (
    <Layout>
      <div className="space-y-5 pb-24" data-testid="psych-profile-page">
        <button data-testid="profile-back" onClick={() => navigate(`/relationship/${id}`)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft size={16} /> Retour
        </button>

        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500/25 to-rose-500/15 flex items-center justify-center text-purple-200 ring-1 ring-white/10">
              <Brain size={22} />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Analyse comportementale</p>
              <h1 className="font-display text-2xl font-extrabold leading-tight mt-0.5">Profil <span className="text-gradient">psychologique</span></h1>
            </div>
          </div>
        </div>

        {/* Target selector */}
        <div className="grid grid-cols-2 gap-2" data-testid="profile-target-toggle">
          {[{ k: "them", label: targetName, icon: Heart }, { k: "me", label: "Moi", icon: Sparkles }].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              data-testid={`profile-target-${k}`}
              onClick={() => { vibrate(); setCompare(false); setTarget(k); }}
              className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold border transition-colors ${
                target === k && !compare ? "bg-purple-500/20 border-purple-400/50 text-purple-100" : "glass-card text-slate-400"
              }`}
            >
              <Icon size={15} /> <span className="truncate max-w-[45vw]">{label}</span>
            </button>
          ))}
        </div>

        <button
          data-testid="profile-compare-toggle"
          onClick={() => { vibrate(); setCompare((c) => !c); }}
          className={`w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold border transition-colors ${
            compare ? "bg-rose-500/20 border-rose-400/50 text-rose-100" : "glass-card text-slate-300 hover:border-rose-400/40"
          }`}
        >
          <Scale size={15} /> {compare ? "Revenir au profil" : `Comparer moi ↔ ${targetName}`}
        </button>

        {compare ? (
          <ComparisonView id={id} contactName={targetName} refCode={refCode} onUpgrade={() => navigate("/pricing?highlight=docteur&from=profile")} onGenerate={(t) => { setCompare(false); setTarget(t); }} />
        ) : loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-purple-400" /></div>
        ) : !p ? (
          <EmptyState data={data} generating={generating} onGenerate={generate} />
        ) : (
          <ProfileBody p={p} targetName={target === "me" ? "toi" : targetName} isMe={target === "me"}
            contactName={targetName} generating={generating} refCode={refCode}
            generatedAt={data?.generated_at} messageCount={data?.message_count}
            onRegen={generate} onUpgrade={() => navigate("/pricing?highlight=pro&from=profile")}
            onPlan={() => navigate("/doctor")} />
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ data, generating, onGenerate }) {
  const can = data?.can_generate;
  return (
    <div className="text-center py-10 rounded-2xl glass-card" data-testid="profile-empty">
      <div className="w-14 h-14 rounded-2xl bg-purple-500/15 flex items-center justify-center text-purple-300 mx-auto mb-4">
        <ShieldQuestion size={26} />
      </div>
      <h3 className="font-display text-lg font-black">Pas encore de profil</h3>
      {can ? (
        <>
          <p className="text-sm text-zinc-500 mt-2 max-w-xs mx-auto">Le Doc va analyser {data?.message_count} messages pour dresser un profil comportemental.</p>
          <button data-testid="profile-generate" onClick={onGenerate} disabled={generating}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-purple-500 hover:bg-purple-600 text-white text-sm font-bold px-6 py-3 disabled:opacity-60">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Brain size={16} />}
            {generating ? "Analyse en cours…" : "Générer le profil"}
          </button>
        </>
      ) : (
        <p className="text-sm text-amber-300/90 mt-2 max-w-xs mx-auto">
          Il faut au moins {data?.min_messages} messages pour un profil fiable ({data?.message_count || 0} pour l'instant). Continue à échanger ou importe une conversation.
        </p>
      )}
    </div>
  );
}

function ComparisonView({ id, contactName, refCode, onUpgrade, onGenerate }) {
  const [state, setState] = useState({ loading: true });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);
  const inviteUrl = refCode ? `${APP_URL}/?ref=${refCode}` : APP_URL;
  const inviteLabel = refCode ? `${APP_DOMAIN}/?ref=${refCode}` : APP_DOMAIN;

  useEffect(() => {
    let alive = true;
    api.get(`/profile/${id}/compare`)
      .then((r) => { if (alive) setState({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setState({ loading: false, error: true }); });
    return () => { alive = false; };
  }, [id]);

  const shareStory = async () => {
    if (!cardRef.current) return;
    setSharing(true); vibrate();
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, skipFonts: true, backgroundColor: "#160a24" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "doclove-comparaison.png", { type: "image/png" });
      const text = `Nos deux profils selon DocLove 👀 Compare les tiens : ${inviteUrl}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Nos deux profils DocLove", text });
        setPreviewOpen(false);
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "doclove-comparaison.png"; a.click();
        toast.success("Image enregistrée — partage-la en story ! 🔥");
        setPreviewOpen(false);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Partage impossible");
    } finally { setSharing(false); }
  };

  if (state.loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-purple-400" /></div>;
  if (state.error) return <p className="text-sm text-zinc-500 text-center py-10">Comparaison indisponible.</p>;

  if (state.locked) {
    const fakeDims = [
      { label: "Emprise", me: 40, them: 85 }, { label: "Contrôle", me: 30, them: 78 },
      { label: "Empathie", me: 82, them: 35 }, { label: "Stabilité", me: 70, them: 40 },
      { label: "Fusion", me: 88, them: 55 }, { label: "Autonomie", me: 45, them: 72 },
    ];
    return (
      <div className="relative rounded-2xl bg-gradient-to-br from-amber-500/15 to-pink-500/15 border border-amber-500/30 overflow-hidden" data-testid="compare-locked">
        {/* Blurred teaser radar behind */}
        <div className="absolute inset-0 blur-[6px] opacity-60 pointer-events-none select-none">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={fakeDims} outerRadius="72%">
              <PolarGrid stroke="rgba(255,255,255,.15)" />
              <Radar dataKey="me" stroke="#f472b6" fill="#ec4899" fillOpacity={0.4} />
              <Radar dataKey="them" stroke="#a78bfa" fill="#8b5cf6" fillOpacity={0.4} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="text-center -mt-6"><span className="font-display text-4xl font-black text-white/80">72%</span></div>
        </div>
        {/* Foreground CTA */}
        <div className="relative bg-black/45 backdrop-blur-[1px] p-6 text-center">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/25 flex items-center justify-center text-amber-200 mx-auto mb-3"><Lock size={22} /></div>
          <h3 className="font-display text-xl font-black">Votre score d'alignement est prêt 🔒</h3>
          <p className="text-sm text-zinc-200 mt-2">
            Superposez vos deux radars, découvrez votre <span className="font-bold text-white">% de compatibilité</span> et là où ça coince vraiment avec {contactName}.
          </p>
          <button data-testid="compare-upgrade" onClick={onUpgrade} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:opacity-90 text-white text-sm font-bold px-6 py-3 shadow-lg shadow-pink-500/20">
            <Crown size={16} /> Débloquer avec Docteur
          </button>
          <p className="text-[11px] text-zinc-400 mt-2">Inclus dans l'offre Docteur · résiliable à tout moment</p>
        </div>
      </div>
    );
  }

  if (state.ready === false) {
    const needMe = state.missing?.includes("me");
    const needThem = state.missing?.includes("them");
    return (
      <div className="rounded-2xl glass-card p-5 text-center" data-testid="compare-notready">
        <Scale size={26} className="text-pink-400 mx-auto mb-3" />
        <h3 className="font-display text-lg font-black">Il manque un profil</h3>
        <p className="text-sm text-zinc-400 mt-2">
          Pour comparer, génère les deux profils : {needMe && "le tien"}{needMe && needThem && " et "}{needThem && `celui de ${contactName}`}.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {needThem && <button data-testid="compare-gen-them" onClick={() => onGenerate("them")} className="rounded-full bg-purple-500 hover:bg-purple-600 text-white text-sm font-bold py-2.5">Générer le profil de {contactName}</button>}
          {needMe && <button data-testid="compare-gen-me" onClick={() => onGenerate("me")} className="rounded-full bg-purple-500 hover:bg-purple-600 text-white text-sm font-bold py-2.5">Générer mon profil</button>}
        </div>
      </div>
    );
  }

  const dims = state.dimensions || [];
  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="rounded-2xl glass-card p-4">
        <p className="text-sm font-bold mb-1">Vos deux profils, superposés</p>
        <p className="text-xs text-zinc-500 mb-2">Plus l'aire est large sur un axe, plus le trait est marqué.</p>
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={dims} outerRadius="72%">
            <PolarGrid stroke="rgba(255,255,255,.12)" />
            <PolarAngleAxis dataKey="label" tick={{ fill: "rgba(255,255,255,.7)", fontSize: 11 }} />
            <Radar name="Moi" dataKey="me" stroke="#f472b6" fill="#ec4899" fillOpacity={0.35} />
            <Radar name={contactName} dataKey="them" stroke="#a78bfa" fill="#8b5cf6" fillOpacity={0.35} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, fontSize: 12 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {state.alignment && (
        <div className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-purple-500/20 p-4 flex items-center gap-4" data-testid="compare-alignment">
          <div className="text-center shrink-0">
            <p className="font-display text-3xl font-black text-white">{state.alignment.score}<span className="text-lg text-zinc-500">%</span></p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">alignement</p>
          </div>
          <p className="text-sm font-semibold text-zinc-200">{state.alignment.verdict}</p>
        </div>
      )}

      <button data-testid="compare-share" onClick={() => { vibrate(); setPreviewOpen(true); }} disabled={sharing}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 text-white text-sm font-bold py-3 disabled:opacity-60">
        <Share2 size={16} /> Partager « Nos deux profils » en story
      </button>

      {(state.top_gaps || []).length > 0 && (
        <div className="rounded-2xl glass-card p-4" data-testid="compare-gaps">
          <p className="text-sm font-bold mb-2">Là où vous divergez le plus</p>
          <ul className="space-y-3">
            {state.top_gaps.map((g) => (
              <li key={g.key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-zinc-300 font-semibold">{g.label}</span>
                  <span className="text-zinc-500">{g.gap > 0 ? `+${g.gap} toi` : `${-g.gap} ${contactName}`}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-pink-500" style={{ width: `${g.me}%` }} /></div>
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-purple-500" style={{ width: `${g.them}%` }} /></div>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex gap-4 mt-3 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-pink-500" /> Moi</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500" /> {contactName}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {[["Moi", state.me, "pink"], [contactName, state.them, "purple"]].map(([name, prof, col]) => (
          <div key={name} className={`rounded-2xl border p-4 ${col === "pink" ? "bg-pink-500/5 border-pink-500/20" : "bg-purple-500/5 border-purple-500/20"}`}>
            <p className={`text-xs font-bold uppercase tracking-wider ${col === "pink" ? "text-pink-300" : "text-purple-300"}`}>{name}</p>
            <p className="text-sm font-display font-black mt-0.5">{prof?.dominant_archetype || "—"}</p>
            <p className="text-xs text-zinc-400 mt-1">{prof?.summary}</p>
          </div>
        ))}
      </div>

      {/* Shareable "Nos deux profils" story card */}
      <AnimatePresence>
        {previewOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => setPreviewOpen(false)}
            data-testid="compare-share-preview"
          >
            <button data-testid="compare-share-close" onClick={() => setPreviewOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>

            <motion.div initial={{ y: 24, scale: 0.96 }} animate={{ y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ width: 288, height: 512, overflow: "hidden" }} className="rounded-3xl shadow-2xl mx-auto">
                <div style={{ transform: "scale(0.8)", transformOrigin: "top left", width: 360, height: 640 }}>
                  <div ref={cardRef} style={{ width: 360, height: 640 }} className="relative overflow-hidden flex flex-col p-6" data-testid="compare-share-card">
                    <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,#160a24 0%,#3b0f4e 55%,#5b0f3a 100%)" }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ top: -64, right: -64, background: "rgba(236,72,153,.4)", filter: "blur(60px)" }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ bottom: -80, left: -64, background: "rgba(139,92,246,.4)", filter: "blur(60px)" }} />

                    <div className="relative flex items-center gap-2 text-white">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Brain size={18} /></div>
                      <span className="font-display text-lg font-black tracking-tight">DocLove</span>
                    </div>

                    <div className="relative mt-5">
                      <p className="text-[11px] font-bold tracking-[0.2em] text-pink-300">NOS DEUX PROFILS</p>
                      <p className="text-white font-display font-black leading-tight mt-1" style={{ fontSize: 26 }}>
                        Moi <span className="text-pink-400">×</span> {(contactName || "?").split(" ")[0]}
                      </p>
                    </div>

                    <div className="relative flex-1 flex items-center justify-center">
                      <RadarChart width={300} height={210} data={dims} outerRadius="72%">
                        <PolarGrid stroke="rgba(255,255,255,.22)" />
                        <PolarAngleAxis dataKey="label" tick={{ fill: "rgba(255,255,255,.85)", fontSize: 10 }} />
                        <Radar dataKey="me" stroke="#f9a8d4" fill="#ec4899" fillOpacity={0.45} />
                        <Radar dataKey="them" stroke="#c4b5fd" fill="#8b5cf6" fillOpacity={0.45} />
                      </RadarChart>
                    </div>

                    {state.alignment && (
                      <div className="relative text-center mb-2">
                        <p className="font-display font-black text-white" style={{ fontSize: 34 }}>{state.alignment.score}%</p>
                        <p className="text-[11px] uppercase tracking-[0.15em] text-white/70 -mt-1">d'alignement</p>
                        <p className="text-sm font-semibold text-pink-200 mt-1">{state.alignment.verdict}</p>
                      </div>
                    )}
                    <div className="relative flex justify-center gap-4 text-[11px] text-white/80 mb-2">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-pink-400" /> Moi</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-400" /> {(contactName || "?").split(" ")[0]}</span>
                    </div>

                    <div className="relative text-center">
                      <p className="font-black text-base" style={{ color: "#f9a8d4" }}>{inviteLabel}</p>
                      {refCode && <p className="text-white/70 text-[11px] mt-0.5">Compare-toi aussi · 1 mois offert 🎁</p>}
                    </div>
                  </div>
                </div>
              </div>

              <button data-testid="compare-share-confirm" onClick={shareStory} disabled={sharing}
                className="w-72 mx-auto mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold py-3 disabled:opacity-60">
                {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                {sharing ? "Préparation…" : "Partager / Enregistrer"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProfileBody({ p, targetName, isMe, contactName, generating, refCode, generatedAt, messageCount, onRegen, onUpgrade, onPlan }) {
  const dims = (p.dimensions || []).map((d) => ({ ...d, label: d.label?.split(" ")[0] }));
  const locked = p.locked;
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [variant, setVariant] = useState(0);
  const inviteUrl = refCode ? `${APP_URL}/?ref=${refCode}` : APP_URL;
  const inviteLabel = refCode ? `${APP_DOMAIN}/?ref=${refCode}` : APP_DOMAIN;
  const theme = archetypeTheme(p);
  const v = VARIANTS[variant % VARIANTS.length];
  const verdict = p.verdict || {};
  const storyBadges = (p.concept_badges || []).slice(0, 3);
  const storyTox = Number(verdict.toxicity) || 0;
  const storyTitle = verdict.title || p.dominant_archetype || "—";
  const storyEmoji = verdict.emoji || theme.emoji;

  // Evolution strip for the story card (only for deep analyses with a real timeline).
  const rawTl = ((p.patterns || {}).sentiment_timeline || []).map((s) => Number(s.score) || 0);
  const storyTl = (() => {
    if (!p.deep || rawTl.length < 3) return null;
    if (rawTl.length <= 6) return rawTl;
    const step = (rawTl.length - 1) / 5;
    return Array.from({ length: 6 }, (_, i) => rawTl[Math.round(i * step)]);
  })();
  // Trend colour: green if the vibe improved, red if it degraded, neutral otherwise.
  const tlDelta = storyTl ? storyTl[storyTl.length - 1] - storyTl[0] : 0;
  const tlColor = !storyTl ? theme.rs : tlDelta >= 8 ? "#34d399" : tlDelta <= -8 ? "#f87171" : theme.rs;
  const tlLabel = tlDelta >= 8 ? "↗ ça s'améliore" : tlDelta <= -8 ? "↘ ça se dégrade" : "→ stable";

  // Weekly nudge: encourage re-generating to watch the curve evolve (retention + shares).
  const daysSince = generatedAt ? Math.floor((Date.now() - new Date(generatedAt).getTime()) / 86400000) : 0;
  const newMsgs = messageCount && p.analyzed_messages ? messageCount - p.analyzed_messages : 0;
  const showNudge = !locked && (daysSince >= 7 || newMsgs >= 20);

  const shareStory = async () => {
    if (!cardRef.current) return;
    setSharing(true); vibrate();
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, skipFonts: true, backgroundColor: theme.g[0] });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "doclove-profil.png", { type: "image/png" });
      const text = `Mon archétype relationnel selon DocLove 👀 Fais ton analyse : ${inviteUrl}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mon profil DocLove", text });
        setPreviewOpen(false);
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "doclove-profil.png"; a.click();
        toast.success("Image enregistrée — partage-la en story ! 🔥");
        setPreviewOpen(false);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Partage impossible");
    } finally { setSharing(false); }
  };

  const copyInvite = async () => {
    vibrate();
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Lien de parrainage copié 🔗");
    } catch (e) {
      toast.error("Copie impossible");
    }
  };

  return (
    <div className="space-y-5">
      {/* Verdict hero — the screenshot-friendly punchline */}
      <VerdictHero p={p} isMe={isMe} contactName={contactName} />

      {/* Synthèse */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 p-5" data-testid="profile-summary">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-wider text-purple-300 font-bold">Archétype dominant</p>
          {p.deep && (
            <span data-testid="profile-deep-badge" className="text-[9px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
              Analyse approfondie · {p.analyzed_messages || data?.message_count} msgs
            </span>
          )}
        </div>
        <p className="text-lg font-display font-black mt-1">{p.dominant_archetype || "—"}</p>
        <p className="text-sm text-zinc-300 mt-2">{p.summary}</p>
      </div>

      <button data-testid="profile-share" onClick={() => { vibrate(); setPreviewOpen(true); }} disabled={sharing}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 text-white text-sm font-bold py-3 disabled:opacity-60">
        <Share2 size={16} /> Partager en story
      </button>

      {/* Radar */}
      <div className="rounded-2xl glass-card p-4" data-testid="profile-radar">
        <p className="text-sm font-bold mb-2">Dimensions comportementales</p>
        <div className={`h-64 ${locked ? "blur-sm pointer-events-none select-none" : ""}`}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={dims} outerRadius="72%">
              <PolarGrid stroke="#3f3f46" />
              <PolarAngleAxis dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
              <Radar dataKey="score" stroke="#a855f7" fill="#a855f7" fillOpacity={0.45} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {locked ? (
        <PaywallCard p={p} onUpgrade={onUpgrade} />
      ) : (
        <>
          {/* Archétypes */}
          <div className="rounded-2xl glass-card p-4" data-testid="profile-archetypes">
            <p className="text-sm font-bold mb-3">Tendances détectées</p>
            <div className="space-y-3">
              {(p.archetypes || []).slice().sort((a, b) => (b.score || 0) - (a.score || 0)).map((a) => {
                const lv = LEVEL[a.level] || LEVEL.low;
                return (
                  <div key={a.key} data-testid={`archetype-${a.key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{a.label}</span>
                      <span className="text-xs font-bold" style={{ color: lv.c }}>{lv.label}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                      <div className={`h-full ${lv.bg} rounded-full transition-all`} style={{ width: `${Math.max(4, a.score || 0)}%` }} />
                    </div>
                    {a.explanation && <p className="text-xs text-zinc-500 mt-1">{a.explanation}</p>}
                    {(a.evidence || []).slice(0, 2).map((ev, i) => (
                      <p key={i} className="text-[11px] text-zinc-400 italic mt-1 pl-2 border-l-2 border-zinc-700">« {ev} »</p>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Flags */}
          <div className="grid grid-cols-1 gap-3">
            <FlagCard title="Signaux de vigilance" tone="red" items={p.flags?.red} />
            <FlagCard title="Signaux sains" tone="green" items={p.flags?.green} />
          </div>

          {/* Patterns */}
          <Patterns patterns={p.patterns} targetName={targetName} />

          {/* Advice */}
          {(p.advice || []).length > 0 && (
            <div className="rounded-2xl glass-card p-4" data-testid="profile-advice">
              <p className="text-sm font-bold mb-2">Conseils du Docteur</p>
              <ul className="space-y-2">
                {p.advice.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Sparkles size={14} className="text-pink-400 shrink-0 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
              <button data-testid="profile-to-plan" onClick={onPlan} className="mt-3 w-full rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold py-2.5 flex items-center justify-center gap-2">
                <Heart size={15} /> Créer un plan d'action avec le Doc
              </button>
            </div>
          )}

          <button data-testid="profile-regenerate" onClick={onRegen} disabled={generating}
            className="w-full flex items-center justify-center gap-2 rounded-2xl glass-card hover:border-purple-500/40 text-sm font-semibold py-3 disabled:opacity-60">
            {generating ? <Loader2 size={15} className="animate-spin text-purple-400" /> : <RefreshCw size={15} className="text-purple-400" />}
            {generating ? "Ré-analyse…" : "Régénérer le profil"}
          </button>

          {showNudge && (
            <div data-testid="profile-weekly-nudge" className="rounded-2xl bg-gradient-to-br from-purple-500/15 to-pink-500/15 border border-purple-500/30 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-200 shrink-0"><TrendingUp size={18} /></div>
              <div className="flex-1">
                <p className="text-sm font-bold">Reviens voir si ça s'améliore 📈</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {newMsgs >= 20
                    ? `${newMsgs} nouveaux messages depuis ta dernière analyse.`
                    : `Ton profil date de ${daysSince} jours.`} Régénère-le pour suivre l'évolution de la courbe.
                </p>
                <button data-testid="profile-nudge-regen" onClick={onRegen} disabled={generating}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold px-4 py-2 disabled:opacity-60">
                  {generating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Mettre à jour mon profil
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-[11px] text-zinc-600 flex items-start gap-1.5" data-testid="profile-disclaimer">
        <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {p.disclaimer}
      </p>

      {/* Preview modal with the shareable story card (9:16) */}
      <AnimatePresence>
        {previewOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => setPreviewOpen(false)}
            data-testid="profile-share-preview"
          >
            <button data-testid="profile-share-close" onClick={() => setPreviewOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>

            <motion.div initial={{ y: 24, scale: 0.96 }} animate={{ y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()}>
              {/* Scaled display wrapper — the inner card stays 360x640 for full-res capture */}
              <div style={{ width: 288, height: 512, overflow: "hidden" }} className="rounded-3xl shadow-2xl mx-auto">
                <div style={{ transform: "scale(0.8)", transformOrigin: "top left", width: 360, height: 640 }}>
                  <div ref={cardRef} style={{ width: 360, height: 640 }} className="relative overflow-hidden flex flex-col p-6" data-testid="profile-share-card" data-variant={variant % VARIANTS.length}>
                    <div className="absolute inset-0" style={{ background: v.bg(theme.g) }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ ...v.a, background: theme.glow[0], filter: "blur(60px)" }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ ...v.b, background: theme.glow[1], filter: "blur(60px)" }} />

                    <div className="relative flex items-center gap-2 text-white">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Brain size={18} /></div>
                      <span className="font-display text-lg font-black tracking-tight">DocLove</span>
                    </div>

                    <div className="relative mt-6">
                      <p style={{ fontSize: 46, lineHeight: 1 }}>{storyEmoji}</p>
                      <p className="text-[11px] font-bold tracking-[0.2em] mt-3" style={{ color: theme.accent }}>MON VERDICT DOCLOVE</p>
                      <p className="text-white font-display font-black leading-tight mt-1" style={{ fontSize: 28 }}>{storyTitle}</p>
                      {verdict.tagline && <p className="text-white/70 text-[12px] mt-1 leading-snug">{verdict.tagline}</p>}
                      {storyTox > 0 && (
                        <div className="mt-3" data-testid="story-toxicity">
                          <div className="flex items-center justify-between text-[10px] text-white/80 mb-1">
                            <span>🌡️ Toxicité</span><span className="font-bold">{storyTox}°</span>
                          </div>
                          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,.18)" }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.max(4, storyTox)}%`, background: storyTox >= 66 ? "#f87171" : storyTox >= 40 ? "#fbbf24" : "#34d399" }} />
                          </div>
                        </div>
                      )}
                      {storyBadges.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="story-badges">
                          {storyBadges.map((b) => (
                            <span key={b.key} className="inline-flex items-center gap-1 rounded-full bg-white/12 text-white text-[10px] font-semibold px-2 py-0.5">
                              <span>{b.emoji}</span>{b.label?.split(" / ")[0]} {b.score}%
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative flex-1 flex items-center justify-center">
                      <RadarChart width={300} height={210} data={dims} outerRadius="72%">
                        <PolarGrid stroke="rgba(255,255,255,.25)" />
                        <PolarAngleAxis dataKey="label" tick={{ fill: "rgba(255,255,255,.85)", fontSize: 10 }} />
                        <Radar dataKey="score" stroke={theme.rs} fill={theme.rf} fillOpacity={0.55} />
                      </RadarChart>
                    </div>

                    <div className="relative text-center">
                      {storyTl && (
                        <div className="mb-3" data-testid="story-evolution">
                          <p className="text-[9px] font-bold tracking-[0.18em] mb-1" style={{ color: theme.accent }}>COMMENT ÇA A ÉVOLUÉ</p>
                          <svg width="300" height="40" viewBox="0 0 300 40" className="mx-auto">
                            <polyline
                              fill="none"
                              stroke={tlColor}
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              points={storyTl.map((s, i) => `${8 + (i * 284) / (storyTl.length - 1)},${34 - (s / 100) * 28}`).join(" ")}
                            />
                            {storyTl.map((s, i) => (
                              <circle key={i} cx={8 + (i * 284) / (storyTl.length - 1)} cy={34 - (s / 100) * 28} r="3" fill="#fff" />
                            ))}
                          </svg>
                          <div className="flex justify-between items-center text-[9px] text-white/70 px-1 -mt-1">
                            <span>Début</span>
                            <span className="font-bold" style={{ color: tlColor }}>{tlLabel}</span>
                            <span>Maintenant</span>
                          </div>
                        </div>
                      )}
                      <p className="text-white/90 text-sm font-semibold">Analyse tes relations, tes red flags & ton style</p>
                      <p className="font-black text-base mt-1" style={{ color: theme.accent }}>{inviteLabel}</p>
                      {refCode && <p className="text-white/70 text-[11px] mt-0.5">Code {refCode} · 1 mois offert 🎁</p>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 w-72 mx-auto">
                <button data-testid="profile-share-variant" onClick={() => { vibrate(); setVariant((x) => x + 1); }}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-white/20 text-white text-sm font-semibold px-4 py-3">
                  <RefreshCw size={15} /> Style
                </button>
                <button data-testid="profile-share-confirm" onClick={shareStory} disabled={sharing}
                  className="flex-1 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold py-3 disabled:opacity-60">
                  {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                  {sharing ? "Préparation…" : "Partager / Enregistrer"}
                </button>
              </div>

              <button data-testid="profile-share-copy-link" onClick={copyInvite}
                className="w-72 mx-auto mt-2 flex items-center justify-center gap-2 rounded-full border border-white/15 text-white/80 hover:text-white text-sm font-semibold py-2.5">
                <Copy size={15} /> Copier mon lien de parrainage
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ATTACH_META = {
  "anxieux": { emoji: "😰", label: "Anxieux" },
  "évitant": { emoji: "🥶", label: "Évitant" },
  "evitant": { emoji: "🥶", label: "Évitant" },
  "sécure": { emoji: "🌿", label: "Sécure" },
  "secure": { emoji: "🌿", label: "Sécure" },
  "désorganisé": { emoji: "🎢", label: "Désorganisé" },
  "desorganise": { emoji: "🎢", label: "Désorganisé" },
};
const attachMeta = (s) => ATTACH_META[(s || "").toLowerCase().trim()] || { emoji: "❓", label: s || "—" };

function toxColor(t) {
  if (t >= 66) return { c: "#f87171", bar: "#ef4444", label: "élevée" };
  if (t >= 40) return { c: "#fbbf24", bar: "#f59e0b", label: "modérée" };
  return { c: "#34d399", bar: "#10b981", label: "faible" };
}

function VerdictHero({ p, isMe, contactName }) {
  const v = p.verdict;
  if (!v || !v.title) return null;
  const tox = Number(v.toxicity) || 0;
  const tc = toxColor(tox);
  const badges = (p.concept_badges || []).filter((b) => (b.score || 0) >= 30);
  const att = p.attachment || {};
  const them = att.them?.style ? attachMeta(att.them.style) : null;
  const me = att.me?.style ? attachMeta(att.me.style) : null;
  const who = isMe ? "toi" : ((contactName || "l'autre").split(" ")[0]);
  const healthy = !v.archetype_key;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      data-testid="profile-verdict-hero"
      className="relative overflow-hidden rounded-2xl border p-5"
      style={{ borderColor: `${tc.c}55`, background: `radial-gradient(120% 90% at 15% 0%, ${tc.c}22 0%, rgba(24,24,27,.6) 55%, #18181b 100%)` }}
    >
      <div className="flex items-start gap-3">
        <div className="text-4xl leading-none shrink-0" data-testid="verdict-emoji">{v.emoji}</div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: tc.c }}>
            {healthy ? "Verdict du Doc" : `Verdict sur ${who}`}
          </p>
          <p className="font-display font-black leading-tight mt-0.5" style={{ fontSize: 24 }} data-testid="verdict-title">
            {v.title}
          </p>
          {v.tagline && <p className="text-sm text-zinc-300 mt-1">{v.tagline}</p>}
        </div>
        {!healthy && (
          <div className="text-right shrink-0">
            <p className="font-display font-black leading-none" style={{ fontSize: 26, color: tc.c }} data-testid="verdict-match">{v.match}%</p>
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">match</p>
          </div>
        )}
      </div>

      {/* Thermomètre de toxicité */}
      <div className="mt-4" data-testid="verdict-toxicity">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-zinc-400 font-semibold">🌡️ Thermomètre de toxicité</span>
          <span className="font-bold" style={{ color: tc.c }}>{tox}° · {tc.label}</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "linear-gradient(90deg,#10b981 0%,#f59e0b 55%,#ef4444 100%)", opacity: 0.28 }}>
          <div className="h-full rounded-full" style={{ width: `${Math.max(4, tox)}%`, background: tc.bar }} />
        </div>
      </div>

      {/* Badges concepts */}
      {badges.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="verdict-badges">
          {badges.map((b) => (
            <span key={b.key} data-testid={`verdict-badge-${b.key}`}
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
              style={{ borderColor: `${(LEVEL[b.level] || LEVEL.low).c}66`, color: (LEVEL[b.level] || LEVEL.low).c, background: `${(LEVEL[b.level] || LEVEL.low).c}14` }}>
              <span>{b.emoji}</span> {b.label} <span className="opacity-70">{b.score}%</span>
            </span>
          ))}
        </div>
      )}

      {/* Combo style d'attachement */}
      {them && me && (
        <div className="mt-4 rounded-xl bg-black/25 border border-white/5 px-3 py-2.5" data-testid="verdict-attachment">
          <div className="flex items-center justify-center gap-2 text-sm font-bold">
            <span>{them.emoji} {them.label}</span>
            <span className="text-zinc-500">↔</span>
            <span>{me.emoji} {me.label}</span>
            {att.me_source === "quiz" && (
              <span data-testid="verdict-attachment-quiz" className="ml-1 text-[9px] font-bold text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded-full px-1.5 py-0.5">🧠 d'après ton test</span>
            )}
          </div>
          {att.dynamic_label && <p className="text-center text-xs font-bold text-pink-300 mt-1">« {att.dynamic_label} »</p>}
          {att.dynamic_desc && <p className="text-center text-[11px] text-zinc-400 mt-0.5">{att.dynamic_desc}</p>}
        </div>
      )}
    </motion.div>
  );
}

function PaywallCard({ p, onUpgrade }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-purple-500/15 to-pink-500/15 border border-purple-500/30 p-5 text-center" data-testid="profile-paywall">
      <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center text-purple-200 mx-auto mb-3"><Lock size={22} /></div>
      {p.teaser_archetype ? (
        <>
          <p className="text-[11px] uppercase tracking-wider text-pink-300 font-bold">Tendance principale détectée</p>
          <p className="font-display text-2xl font-black mt-0.5">{p.teaser_archetype.label}</p>
          <span className="inline-block mt-1 text-xs font-bold text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-0.5">
            niveau {LEVEL[p.teaser_archetype.level]?.label}
          </span>
        </>
      ) : (
        <h3 className="font-display text-xl font-black">Ton profil complet est prêt 🔒</h3>
      )}
      <p className="text-sm text-zinc-300 mt-3">
        <span className="font-bold text-white">{p.archetype_count} tendances</span> analysées (pervers narcissique, love bombing, dépendance…), avec <span className="text-white">preuves, red/green flags, radar et conseils</span> personnalisés.
      </p>
      <button data-testid="profile-upgrade" onClick={onUpgrade} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white text-sm font-bold px-6 py-3 shadow-lg shadow-purple-500/20">
        <Crown size={16} /> Débloquer mon profil
      </button>
      <p className="text-[11px] text-zinc-400 mt-2">Dès 9,99€/mois · analyses illimitées · résiliable à tout moment</p>
      <div className="mt-3 flex justify-center"><SocialProof /></div>
    </motion.div>
  );
}

function FlagCard({ title, tone, items }) {
  if (!items || items.length === 0) return null;
  const red = tone === "red";
  return (
    <div className={`rounded-2xl border p-4 ${red ? "bg-red-500/5 border-red-500/20" : "bg-emerald-500/5 border-emerald-500/20"}`} data-testid={`profile-flags-${tone}`}>
      <p className={`text-sm font-bold mb-2 ${red ? "text-red-300" : "text-emerald-300"}`}>{title}</p>
      <ul className="space-y-2">
        {items.map((f, i) => (
          <li key={i} className="text-sm text-zinc-300">
            <span className="flex items-start gap-2">{red ? <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" /> : <Heart size={14} className="text-emerald-400 shrink-0 mt-0.5" />} {f.text}</span>
            {f.evidence && <p className="text-[11px] text-zinc-500 italic mt-0.5 pl-6">« {f.evidence} »</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Patterns({ patterns, targetName }) {
  if (!patterns) return null;
  const init = patterns.initiation || {};
  const pie = [
    { name: "Moi", value: Number(init.me) || 0, c: "#ec4899" },
    { name: targetName, value: Number(init.them) || 0, c: "#a855f7" },
  ];
  const timeline = (patterns.sentiment_timeline || []).map((s) => ({ label: s.label, score: Number(s.score) || 0 }));
  return (
    <div className="rounded-2xl glass-card p-4 space-y-4" data-testid="profile-patterns">
      <p className="text-sm font-bold">Dynamique des échanges</p>
      {(pie[0].value || pie[1].value) ? (
        <div className="flex items-center gap-4">
          <div className="w-24 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={26} outerRadius={44} paddingAngle={2}>
                  {pie.map((e, i) => <Cell key={i} fill={e.c} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-sm">
            <p className="text-zinc-400 mb-1">Qui initie les conversations</p>
            <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-pink-500 inline-block" /> Moi · {pie[0].value}%</p>
            <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-500 inline-block" /> {targetName} · {pie[1].value}%</p>
          </div>
        </div>
      ) : null}
      {timeline.length > 0 && (
        <div>
          <p className="text-xs text-zinc-500 mb-1">Évolution du climat</p>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline}>
                <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 10 }} />
                <YAxis domain={[0, 100]} hide />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {patterns.response_style && <p className="text-sm text-zinc-300">{patterns.response_style}</p>}
      {(patterns.recurring_themes || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {patterns.recurring_themes.map((t, i) => (
            <span key={i} className="text-xs rounded-full bg-zinc-800 text-zinc-300 px-2.5 py-1">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
